'use client'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { SlidersHorizontal, BrainCircuit, Gamepad2, CalendarDays, Users, MonitorPlay, PhoneCall, MapPin, BookOpenText, Award, Shield, TrendingUp, Globe2, ArrowRight, Sparkles } from 'lucide-react'
import type { Locale } from '@/lib/common-content'
import { ConsultationCTASection } from '@/components/ConsultationCTASection'
import { PageHeroEyebrow, pageHeroLeadClass, pageHeroTitleClass } from '@/components/PageHeroEyebrow'
import { PageDecor, ClassroomPhotoStrip } from '@/components/PageDecor'
import { openApply } from '@/components/apply/ApplyDialog'

const content = {
  en: {
    label: 'Kids Program',
    title: 'Japanese Language Academy For Kids',
    description: 'Children aged 9–12 learn Japanese through games, songs, origami and stories, on campus, online or at weekends.',
    admissionsNote: 'Admissions Open for 2026/27 Intakes',
    ctaLabel: 'Next Step',
    btnLabel: 'Apply Now',
    contactTitle: 'Need assistance choosing the right slot?',
    contactDesc: 'Our support team is ready to help you at +92 300 1301223',
    callBtn: 'Call Us Now',
    roadmap: {
      y1: '1 Year', y1Desc: 'Scratch to JLPT N5.',
      y2: '2 Years', y2Desc: 'Scratch to JLPT N4.',
      custom: 'Custom Plan', customDesc: 'You choose the pace, schedule and goal.', customCta: 'Plan for my child',
      customSubject: 'Kids Program: Custom Plan request',
    },
    schedules: [
      { title: "On Campus Classes", time: "Mon & Thu | 4:00 - 5:00 PM", location: "Al-Hafeez Suites, off MM Alam Road, 69/B-2 Block B 2 Gulberg III, Lahore, 54000, Second Floor, Room # 609", desc: "In-person learning for the 2026/27 intakes." },
      { title: "Online Evening Sessions", time: "Tue & Wed | 4:00 - 5:00 PM", location: "Live Virtual Classroom", desc: "Interactive virtual classrooms for the 2026/27 intakes." },
      { title: "Weekend Sessions", time: "Saturday | 2:00 - 3:00 PM", location: "Al-Hafeez Suites, off MM Alam Road, 69/B-2 Block B 2 Gulberg III, Lahore, 54000, Second Floor, Room # 609 & Online", desc: "Focused weekend boost for the 2026/27 intakes." }
    ],
    cards: [
      { title: "INTERACTIVE", desc: "Gamified learning experiences that keep kids engaged throughout their journey." },
      { title: "NATIVE MENTORS", desc: "Guidance from native speakers to ensure authentic pronunciation and culture." },
      { title: "COGNITIVE", desc: "Structured curriculum designed to boost memory and problem-solving skills." },
      { title: "GLOBAL", desc: "Broadening horizons through exposure to Japanese traditions and customs." },
      { title: "SECURE", desc: "A safe, monitored environment ensuring the wellbeing of all students." },
      { title: "PROGRESS", desc: "Regular assessments to track linguistic and personal growth milestones." }
    ]
  },
  ja: {
    label: 'キッズプログラム',
    title: 'キッズ語学アカデミー',
    description: '9〜12歳のお子さまが、ゲーム・歌・折り紙・おはなしで日本語を学びます。通学・オンライン・週末クラスあり。',
    admissionsNote: '2026/27年度入学 - 出願受付中',
    ctaLabel: '次のステップ',
    btnLabel: '今すぐ申し込む',
    contactTitle: 'スロットの選択でお困りですか？',
    contactDesc: 'サポートチームが対応します：+92 300 1301223',
    callBtn: '今すぐ電話する',
    roadmap: {
      y1: '1年', y1Desc: '基礎トレーニング（JLPT N5）',
      y2: '2年', y2Desc: '高度な言語開発（JLPT N4）',
      custom: 'カスタムプラン', customDesc: 'ペース・時間・目標を保護者が設定', customCta: 'プランを相談する',
      customSubject: 'キッズプログラム：カスタムプランの相談',
    },
    schedules: [
      { title: "キャンパス対面クラス", time: "月・木 | 16:00 - 17:00", location: "ラホール、グルバーグIII、MMアラムロード、アル・ハフィーズ・スイーツ、69/B-2 Block B 2, 54000, 2階, 609号室", desc: "2026/27年度開講の対面学習。" },
      { title: "オンライン夜間セッション", time: "火・水 | 16:00 - 17:00", location: "ライブ・バーチャル教室", desc: "2026/27年度開講のインタラクティブなオンラインクラス。" },
      { title: "週末セッション", time: "土 | 14:00 - 15:00", location: "アル・ハフィーズ・スイーツ (校舎 & オンライン)", desc: "2026/27年度開講、学習を強化する週末ブーストセッション。" }
    ],
    cards: [
      { title: "インタラクティブ", desc: "子供たちが夢中になるゲーミフィケーション学習。" },
      { title: "ネイティブ講師", desc: "本物の発音と文化を学ぶネイティブ指導。" },
      { title: "認知能力", desc: "記憶力と問題解決能力を向上させるカリキュラム。" },
      { title: "グローバル", desc: "日本の伝統と習慣への触れ合い。" },
      { title: "安全", desc: "すべての生徒の安全を確保する監視環境。" },
      { title: "成長", desc: "言語と個人の成長を追跡する定期評価。" }
    ]
  }
} as const

const ICONS = [Gamepad2, Users, BrainCircuit, Globe2, Shield, TrendingUp]
const ICON_STYLES = [
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", hoverBorder: "hover:border-brand-red/40", ring: "group-hover:ring-brand-red-soft" },
  { text: "text-brand-green", bg: "bg-brand-green-soft", hoverBorder: "hover:border-brand-green/40", ring: "group-hover:ring-brand-green-soft" },
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", hoverBorder: "hover:border-brand-red/40", ring: "group-hover:ring-brand-red-soft" },
  { text: "text-brand-green", bg: "bg-brand-green-soft", hoverBorder: "hover:border-brand-green/40", ring: "group-hover:ring-brand-green-soft" },
]

export function KidsPageView({ locale }: { locale: Locale }) {
  const copy = content[locale] || content['en']
  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.15 } } }
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }
  const scheduleIcons = [Users, MonitorPlay, CalendarDays]

  return (
    <div className="relative w-full py-12 md:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <PageDecor />

      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative max-w-6xl mx-auto space-y-16">
        <motion.header variants={itemVariants} className="text-center space-y-10">
          <div className="group space-y-5">
            <PageHeroEyebrow>{copy.label}</PageHeroEyebrow>
            <h1 className={pageHeroTitleClass}>{copy.title}</h1>
            <p className={`${pageHeroLeadClass} max-w-xl`}>{copy.description}</p>

            <motion.button
              type="button"
              onClick={() => openApply('japanese-for-kids')}
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              className="btn-primary gap-2 mt-2 !py-2 !px-5 !text-sm"
            >
              <Sparkles className="w-4 h-4" />
              {copy.admissionsNote}
            </motion.button>
          </div>
          <div className="flex items-start justify-center max-w-2xl mx-auto">
            <motion.button type="button" onClick={() => openApply('japanese-for-kids')} whileHover={{ y: -4 }} className="group flex-1 flex flex-col items-center text-center gap-3 rounded-2xl p-2 focus-ring">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-brand-navy flex items-center justify-center shadow-sm"><BookOpenText className="w-6 h-6 text-brand-charcoal" /></div>
              <p className="text-xs font-bold text-brand-charcoal-80 uppercase tracking-widest">{copy.roadmap.y1}</p>
              <p className="text-sm font-semibold text-brand-charcoal-80 max-w-[130px] leading-snug">{copy.roadmap.y1Desc}</p>
              <span className="text-xs font-semibold text-brand-red group-hover:underline">{copy.btnLabel} →</span>
            </motion.button>
            <div className="w-16 sm:w-24 h-0.5 bg-brand-cool-gray mt-7 shrink-0" />
            <motion.button type="button" onClick={() => openApply('japanese-for-kids')} whileHover={{ y: -4 }} className="group flex-1 flex flex-col items-center text-center gap-3 rounded-2xl p-2 focus-ring">
              <div className="w-14 h-14 rounded-full bg-brand-red flex items-center justify-center shadow-md"><Award className="w-6 h-6 text-white" /></div>
              <p className="text-xs font-bold text-brand-red uppercase tracking-widest">{copy.roadmap.y2}</p>
              <p className="text-sm font-semibold text-brand-charcoal-80 max-w-[130px] leading-snug">{copy.roadmap.y2Desc}</p>
              <span className="text-xs font-semibold text-brand-red group-hover:underline">{copy.btnLabel} →</span>
            </motion.button>
            <div className="w-10 sm:w-24 h-0.5 bg-brand-cool-gray mt-7 shrink-0" />
            {/* Custom plan: parents set pace, schedule and goal */}
            <div className="flex-1 flex flex-col items-center text-center gap-3 p-2">
              <div className="w-14 h-14 rounded-full bg-brand-green flex items-center justify-center shadow-md"><SlidersHorizontal className="w-6 h-6 text-white" /></div>
              <p className="text-xs font-bold text-brand-green uppercase tracking-widest">{copy.roadmap.custom}</p>
              <p className="text-sm font-semibold text-brand-charcoal-80 max-w-[150px] leading-snug">{copy.roadmap.customDesc}</p>
              <Link
                href={`/${locale}/contact?subject=${encodeURIComponent(copy.roadmap.customSubject)}`}
                className="btn-primary !px-4 !py-2 !text-xs"
              >
                {copy.roadmap.customCta}
              </Link>
            </div>
          </div>
          <ClassroomPhotoStrip className="pt-2" />
        </motion.header>

        {/* Divider to separate header from the schedule section below */}
        <div className="flex items-center gap-4 max-w-3xl mx-auto">
          <span className="flex-1 h-px bg-brand-cool-gray" />
          <span className="w-2 h-2 rounded-full bg-brand-cool-gray" />
          <span className="flex-1 h-px bg-brand-cool-gray" />
        </div>

        {/* Schedule Section */}
        <motion.div variants={itemVariants} className="bg-white rounded-3xl border border-brand-cool-gray shadow-sm overflow-hidden flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-brand-cool-gray">
           {copy.schedules.map((item, i) => {
             const Icon = scheduleIcons[i]
             return (
               <motion.button type="button" onClick={() => openApply('japanese-for-kids')} key={i} whileHover={{ backgroundColor: "#F2F4F5" }} transition={{ duration: 0.25 }} className="group p-8 flex flex-col gap-4 flex-1 text-left focus-ring">
                 <Icon className="w-8 h-8 text-brand-green" />
                 <h3 className="text-lg font-semibold tracking-tight text-brand-charcoal">{item.title}</h3>
                 <p className="text-sm font-semibold tracking-tight text-brand-charcoal-80 bg-brand-soft-gray p-2 rounded w-fit">{item.time}</p>
                 <div className="flex items-start gap-2 text-brand-muted font-medium"><MapPin className="w-4 h-4 mt-0.5 text-brand-red shrink-0" /><p className="text-xs leading-tight">{item.location}</p></div>
                 <p className="flex-1 text-sm text-brand-muted leading-relaxed">{item.desc}</p>
                 <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                   {copy.btnLabel}
                   <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                 </span>
               </motion.button>
             )
           })}
        </motion.div>

        {/* Register Button: Adjusted with negative margin to pull it closer to schedule */}
        <motion.div variants={itemVariants} className="flex justify-center -mt-8 mb-4">
            <motion.button type="button" onClick={() => openApply('japanese-for-kids')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn-primary gap-3 !px-10 !py-4 md:!text-lg">
                {copy.btnLabel} <ArrowRight className="w-5 h-5" />
            </motion.button>
        </motion.div>

        {/* Cards Section */}
        <motion.div variants={containerVariants} className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 pt-10">
          {copy.cards.map((card, i) => {
            const Icon = ICONS[i]
            const style = ICON_STYLES[i]
            return (
              <motion.div key={card.title} variants={itemVariants} className="group relative p-8 bg-white border border-brand-cool-gray rounded-3xl shadow-sm">
                <span className="absolute top-6 right-7 text-xs font-bold text-brand-cool-gray tracking-widest">0{i + 1}</span>
                <div className={`mb-6 w-14 h-14 flex items-center justify-center rounded-2xl transition-all duration-300 ${style.bg} ring-4 ring-transparent ${style.ring} group-hover:scale-105`}>
                  <Icon className={`w-7 h-7 ${style.text}`} />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-brand-charcoal mb-3">{card.title}</h3>
                <p className="text-brand-muted text-sm leading-relaxed font-light">{card.desc}</p>
              </motion.div>
            )
          })}
        </motion.div>

        <motion.div variants={itemVariants}>
          <ConsultationCTASection
            eyebrow={copy.ctaLabel}
            title={copy.contactTitle}
            description={copy.contactDesc}
            accent="emerald"
            actions={
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="tel:+923001301223"
                className="btn-primary gap-3 whitespace-nowrap"
              >
                <PhoneCall className="w-5 h-5" />
                {copy.callBtn}
              </motion.a>
            }
          />
        </motion.div>
      </motion.div>
    </div>
  )
}